import type { BrandAssets, BrandColor, BrandFont } from '@mg/shared';

/** A blank color row used when adding a new swatch. */
export function emptyColor(): BrandColor {
  return { name: '', hex: '#000000', usage: '' };
}

/** A typography role pre-filled with the app font so the field is editable, not blank. */
function defaultFont(usage: string): BrandFont {
  return { fontFamily: 'Inter', weight: 'Regular (400)', usage };
}

/**
 * Sensible starter brand assets for a workspace that has none yet, so the editor
 * renders pre-populated, editable sections rather than empty shells.
 */
export function defaultBrandAssets(): BrandAssets {
  return {
    colors: {
      primary: [
        { name: 'Primary', hex: '#0FB8F0', usage: 'Headlines, primary buttons' },
        { name: 'Accent', hex: '#0FB8F0', usage: 'Links, highlights' },
      ],
      secondary: [{ name: 'Neutral', hex: '#1f2937', usage: 'Body text' }],
    },
    typography: {
      headings: defaultFont('Headlines and section titles'),
      body: defaultFont('Paragraph and UI text'),
      accent: defaultFont('Callouts and quotes'),
    },
    hashtags: [],
    phrases: [],
    logos: { primary: null, dark: null, icon: null },
  };
}

/** A value read from the stored JSON, whose shape nothing guarantees. */
type Loose = Record<string, unknown>;

const isObject = (value: unknown): value is Loose =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** A stored string, or the fallback when the value is missing or not a string. */
const text = (value: unknown, fallback: string): string => (typeof value === 'string' ? value : fallback);

/** A color group: each row copied, a row's missing text filled in; not a list → the starter rows. */
function initColorRows(rows: unknown, starter: BrandColor[]): BrandColor[] {
  if (!Array.isArray(rows)) return starter;
  return rows.filter(isObject).map((row) => ({
    ...row,
    name: text(row.name, ''),
    hex: text(row.hex, '#000000'),
    usage: text(row.usage, ''),
  }));
}

/** A typography role: copied, a missing part from the starter role; not an object → the starter role. */
function initFont(role: unknown, starter: BrandFont): BrandFont {
  if (!isObject(role)) return starter;
  return {
    ...role,
    fontFamily: text(role.fontFamily, starter.fontFamily),
    weight: text(role.weight, starter.weight),
    usage: text(role.usage, starter.usage),
  };
}

/** A stored list of strings, copied; anything else → empty. */
const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

/** A logo slot: its proxy URL, or none. */
const logo = (value: unknown): string | null => (typeof value === 'string' ? value : null);

/**
 * Clone existing brand assets (deeply enough for editing) or fall back to the
 * defaults when the workspace has none. The result is safe to mutate via setState.
 *
 * Tolerant of a PARTIAL object (final-design §3 X27): with Brand Workspace v2 the Brand information
 * page writes only `hashtags` and `phrases` into `brandAssets`, so a workspace that had none holds
 * `{ hashtags, phrases }` alone, and the old page meets it after a rollback or a scoped migration
 * apply. Each missing or malformed key falls back to its starter value for display, as a missing
 * object always has (colors, a color group, a typography role or one of its parts); a missing list is
 * empty and a missing logo is none. A complete object comes back equal to what was stored.
 */
export function initBrandAssets(existing: Partial<BrandAssets> | null | undefined): BrandAssets {
  const starter = defaultBrandAssets();
  const stored: unknown = existing;
  if (!isObject(stored)) return starter;
  const colors = isObject(stored.colors) ? stored.colors : null;
  const typography = isObject(stored.typography) ? stored.typography : null;
  return {
    colors: colors
      ? {
          primary: initColorRows(colors.primary, starter.colors.primary),
          secondary: initColorRows(colors.secondary, starter.colors.secondary),
        }
      : starter.colors,
    typography: typography
      ? {
          headings: initFont(typography.headings, starter.typography.headings),
          body: initFont(typography.body, starter.typography.body),
          accent: initFont(typography.accent, starter.typography.accent),
        }
      : starter.typography,
    hashtags: strings(stored.hashtags),
    phrases: strings(stored.phrases),
    logos: isObject(stored.logos)
      ? {
          ...stored.logos,
          primary: logo(stored.logos.primary),
          dark: logo(stored.logos.dark),
          icon: logo(stored.logos.icon),
        }
      : starter.logos,
  };
}
